import { useState, useEffect, type FormEvent } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export default function Contact() {
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activity, setActivity] = useState('Hoppborg');
  const [meddelande, setMeddelande] = useState('');
  const [formKey, setFormKey] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const aktivitet = params.get('aktivitet');
    if (aktivitet) {
      setMeddelande(`Hej! Jag är intresserad av att boka "${aktivitet}".`);
    }
  }, []);

  const reset = () => {
    setSent(false);
    setError(null);
    setActivity('Hoppborg');
    setMeddelande('');
    setFormKey((k) => k + 1);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const data = new FormData(e.currentTarget);
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          namn: data.get('namn'),
          email: data.get('email'),
          tel: data.get('tel'),
          datum: data.get('datum'),
          aktivitet: activity,
          meddelande: data.get('meddelande'),
        }),
      });
      if (!res.ok) {
        const json = await res.json() as { error?: string };
        throw new Error(json.error ?? 'Något gick fel');
      }
      setSent(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="kontakt" className="relative py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12">
          {/* Info */}
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 rounded-full bg-ink/5 border border-ink/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-ink/70">
              <span className="size-1.5 rounded-full bg-grass" />
              Boka eller fråga
            </div>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.02] text-balance">
              Redo att <span className="text-tomato">studsa</span> igång?
            </h2>
            <p className="mt-5 text-lg text-ink/65 max-w-md text-pretty">
              Skicka en förfrågan så svarar vi inom 24 timmar — eller ring oss direkt. Vi älskar att prata om event.
            </p>

            <div className="mt-8 space-y-3">
              <ContactCard
                icon="📍"
                title="Besök oss"
                lines={['Parkvägen 4', '560 27 Tenhult, Sverige']}
              />
              <ContactCard
                icon="📞"
                title="Ring oss"
                lines={['036-12 34 56 (kontor)', '070-123 45 67 (jour)']}
              />
              <ContactCard
                icon="✉️"
                title="Maila oss"
                lines={['info@borgakungen.se', 'event@borgakungen.se']}
              />
            </div>

            <div className="mt-6 overflow-hidden rounded-3xl border border-ink/10 shadow-pop aspect-[16/9]">
              <iframe
                title="Karta till Borgakungen"
                src="https://www.openstreetmap.org/export/embed.html?bbox=14.41%2C57.70%2C14.49%2C57.74&layer=mapnik&marker=57.72%2C14.45"
                className="h-full w-full"
                loading="lazy"
              />
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <motion.form
              key={formKey}
              onSubmit={handleSubmit}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative rounded-[2rem] bg-cream border-2 border-ink shadow-bouncy overflow-hidden"
            >
              {/* Form content — blurs on success */}
              <motion.div
                animate={{ filter: sent ? 'blur(4px)' : 'blur(0px)', opacity: sent ? 0.35 : 1 }}
                transition={{ duration: 0.5 }}
                className="p-6 sm:p-10"
              >
                <div className="absolute -top-5 -right-5 hidden sm:flex size-24 rounded-full bg-sun border-4 border-cream shadow-pop items-center justify-center rotate-12">
                  <span className="text-3xl" aria-hidden>🎉</span>
                </div>

                <h3 className="font-display text-3xl font-semibold">Förfrågan</h3>
                <p className="text-sm text-ink/60 mt-1">Vi svarar inom 24 timmar.</p>

                <div className="mt-6 grid sm:grid-cols-2 gap-4">
                  <Field label="Namn" name="namn" placeholder="Anna Andersson" required />
                  <Field label="E-post" name="email" type="email" placeholder="anna@exempel.se" required />
                  <Field label="Telefon" name="tel" type="tel" placeholder="070-123 45 67" />
                  <Field label="Datum" name="datum" type="date" />
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-semibold mb-2">Aktivitet</label>
                  <div className="flex flex-wrap gap-2">
                    {['Hoppborg', 'Hinderbana', 'Spel', 'Mat & Mys', 'Annat'].map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => setActivity(opt)}
                        className={`rounded-full px-4 py-2 text-sm font-medium border-2 transition-all ${
                          activity === opt
                            ? 'bg-ink text-cream border-ink'
                            : 'border-ink/15 hover:border-ink hover:bg-sun/40'
                        }`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-4">
                  <label htmlFor="meddelande" className="block text-sm font-semibold mb-2">
                    Meddelande
                  </label>
                  <textarea
                    id="meddelande"
                    name="meddelande"
                    rows={4}
                    value={meddelande}
                    onChange={(e) => setMeddelande(e.target.value)}
                    placeholder="Berätta om eventet, antalet barn, plats m.m."
                    className="w-full rounded-2xl border-2 border-ink/15 bg-cream px-4 py-3 text-base focus:outline-none focus:border-ink transition-colors"
                  />
                </div>

                {error && (
                  <p className="mt-4 text-sm text-tomato font-medium">{error}</p>
                )}

                <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                  <p className="text-xs text-ink/55">
                    Genom att skicka godkänner du vår integritetspolicy.
                  </p>
                  <button
                    type="submit"
                    disabled={loading || sent}
                    className="group inline-flex items-center justify-center gap-2 rounded-full bg-tomato text-cream px-7 py-4 text-base font-semibold shadow-bouncy hover:-translate-y-0.5 transition-transform disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                  >
                    {loading ? 'Skickar…' : (<>Skicka förfrågan <span className="transition-transform group-hover:translate-x-1">→</span></>)}
                  </button>
                </div>
              </motion.div>

              {/* Success overlay */}
              <AnimatePresence>
                {sent && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.4, delay: 0.15 }}
                    className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-5 p-8 text-center"
                  >
                    <button
                      type="button"
                      onClick={reset}
                      className="absolute top-4 right-4 size-9 rounded-full bg-ink/8 hover:bg-ink/15 grid place-items-center transition-colors"
                      aria-label="Stäng och skicka ny förfrågan"
                    >
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </button>

                    <motion.div
                      initial={{ scale: 0.6, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ type: 'spring', stiffness: 300, damping: 20, delay: 0.25 }}
                      className="size-20 rounded-full bg-grass border-4 border-cream shadow-pop grid place-items-center text-4xl"
                    >
                      ✓
                    </motion.div>
                    <motion.div
                      initial={{ y: 12, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ duration: 0.4, delay: 0.35 }}
                    >
                      <p className="font-display text-2xl sm:text-3xl font-semibold text-ink">
                        Tack, din förfrågan är inskickad!
                      </p>
                      <p className="mt-2 text-ink/60 text-base">Vi hör av oss inom 24 timmar.</p>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.form>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactCard({ icon, title, lines }: { icon: string; title: string; lines: string[] }) {
  return (
    <div className="flex gap-4 rounded-2xl bg-cream border border-ink/10 p-4 shadow-[0_6px_18px_-12px_rgba(15,27,45,0.25)]">
      <div className="size-12 shrink-0 grid place-items-center rounded-2xl bg-sun text-xl">{icon}</div>
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-ink/55">{title}</div>
        {lines.map((l) => (
          <div key={l} className="text-[15px] font-medium leading-snug">{l}</div>
        ))}
      </div>
    </div>
  );
}

function Field({
  label,
  name,
  type = 'text',
  placeholder,
  required,
}: {
  label: string;
  name: string;
  type?: string;
  placeholder?: string;
  required?: boolean;
}) {
  return (
    <div>
      <label htmlFor={name} className="block text-sm font-semibold mb-2">
        {label} {required && <span className="text-tomato">*</span>}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        placeholder={placeholder}
        required={required}
        className="w-full rounded-2xl border-2 border-ink/15 bg-cream px-4 py-3 text-base focus:outline-none focus:border-ink transition-colors"
      />
    </div>
  );
}
